/**
 * Mobile navigation drawer.
 *
 * Hamburger toggle opent een rechts-uitschuivend drawer panel met
 * focus-trap en scroll-lock. Altijd overlay — eenvoudiger dan de
 * archive panel-infra die ook responsive en non-overlay modes
 * ondersteunt.
 *
 * Multi-toggle support: meerdere `[data-agora-menu-toggle]` buttons
 * kunnen tegelijk in de DOM staan — één in de echte header, één in
 * de sticky header (en eventueel een derde voor minimal-layout). Alle
 * toggles bedienen hetzelfde `[data-agora-mobile-nav]` panel en hun
 * `aria-expanded` state wordt gesynchroniseerd.
 *
 * Geen koppeling met de archive-url module: aparte instance, eigen
 * focus-trap, eigen state. Hergebruik komt pas wanneer een derde
 * consumer (bv. een SearchOverlay module) zich aandient.
 *
 * @module modules/ui/mobile-nav
 */

import { lockScroll, unlockScroll } from './scroll-lock.js';
import { createFocusTrap } from './focus-trap.js';
import { fadeOut, motionAnimate } from './motion-utils.js';

// Selector voor de menu-items die staggered in-faden bij open.
// Pakt top-level primary items, secondary nav items én de optionele
// CTA-wrapper. Submenu-items zijn bewust niet meegenomen — die zijn
// via accordion-toggle bereikbaar en zouden een te druk effect geven.
const STAGGER_SELECTOR =
    '.mobile-nav__menu > li, .mobile-nav-secondary__menu > li, .mobile-nav__cta-wrapper';

const SELECTORS = {
    toggle: '[data-agora-menu-toggle]',
    panel: '[data-agora-mobile-nav]',
    drawer: '.mobile-nav__drawer',
};

// Matcht `respond-to('md')` uit `01-settings/_breakpoints.scss`. Het
// matchMedia-`change`-event vuurt alleen als de boundary wordt
// overschreden — geen continue resize-storm zoals bij `window.resize`.
const DESKTOP_QUERY = '(min-width: 768px)';

let focusTrap = null;

// Animation guard: voorkomt dat een tweede klik tijdens de open- of
// close-animatie (slide-in + stagger ~300ms, fade-out ~200ms) een
// dubbele toggle triggert. Tijdens animatie staat `panel.hidden` nog
// niet op de eind-state, dus de naïeve `!panel.hasAttribute('hidden')`
// check zou anders flickerend gedrag opleveren. Reset altijd via
// `try/finally` zodat een gefaalde animatie de UI niet permanent
// vergrendelt.
let isAnimating = false;

/**
 * Public init: bindt elke toggle, plus document-level Escape en
 * viewport-resize handlers. Idempotent — herhaalde calls op dezelfde
 * toggle binden niet opnieuw.
 *
 * Graceful degradation: zonder toggle of panel in de DOM doet de
 * functie niets.
 *
 * @returns {void}
 */
export function initMobileNav() {
    const toggles = document.querySelectorAll(SELECTORS.toggle);
    const panel = document.querySelector(SELECTORS.panel);
    if (toggles.length === 0 || !(panel instanceof HTMLElement)) return;

    // De hamburger is tegelijk open- én close-button: bij open staat
    // animeert hij naar een X (zie `_menu-toggle.scss` aria-expanded
    // styling) en hij zweeft via z-index boven de fullscreen drawer.
    toggles.forEach((toggle) => {
        if (!(toggle instanceof HTMLElement)) return;
        if (toggle.dataset.agoraMobileNavBound === '1') return;
        toggle.dataset.agoraMobileNavBound = '1';

        toggle.addEventListener('click', () => {
            if (isAnimating) return;
            const isOpen = !panel.hasAttribute('hidden');
            setPanelState(!isOpen, toggles, panel);
        });
    });

    // Escape sluit; focus terug naar de toggle die zichtbaar is in de
    // viewport (anders verdwijnt focus op de verborgen sticky toggle).
    document.addEventListener('keydown', (e) => {
        if (e.key !== 'Escape') return;
        if (isAnimating) return;
        const isOpen = Array.from(toggles).some(
            (t) => t instanceof HTMLElement && t.getAttribute('aria-expanded') === 'true',
        );
        if (!isOpen) return;
        setPanelState(false, toggles, panel);
        const visible = getVisibleToggle(toggles);
        if (visible) visible.focus();
    });

    // Sluit de drawer wanneer het viewport boven de `md` breakpoint
    // komt. Anders blijft een open drawer fullscreen over de desktop
    // layout liggen na resize/rotatie. We luisteren op het matchMedia
    // `change`-event (boundary-crossing) ipv `resize` (continue storm)
    // — pas reageren wanneer de query state daadwerkelijk wisselt.
    if (typeof window.matchMedia === 'function') {
        const mql = window.matchMedia(DESKTOP_QUERY);
        const handleViewportChange = (e) => {
            if (!e.matches) return;
            if (isAnimating) return;
            const isOpen = Array.from(toggles).some(
                (t) => t instanceof HTMLElement && t.getAttribute('aria-expanded') === 'true',
            );
            if (!isOpen) return;
            setPanelState(false, toggles, panel);
        };
        mql.addEventListener('change', handleViewportChange);
    }
}

/**
 * Toggle panel open/dicht en synct alle bijbehorende state:
 * `aria-expanded` op alle toggles, `hidden` op het panel, body
 * scroll-lock en focus-trap.
 *
 * @param {boolean}                                 open
 * @param {NodeListOf<Element>|HTMLElement[]}       toggles
 * @param {HTMLElement}                             panel
 *
 * @returns {void}
 */
async function setPanelState(open, toggles, panel) {
    // Lock guard direct aan het begin zodat de hele open/close flow
    // — inclusief de awaits — atomair lijkt vanuit het oogpunt van
    // event-handlers. `try/finally` zorgt dat een gefaalde animatie
    // (bv. wanneer Motion.js een rejected promise teruggeeft) de UI
    // niet permanent vergrendeld laat.
    isAnimating = true;
    try {

    toggles.forEach((t) => {
        if (t instanceof HTMLElement) {
            t.setAttribute('aria-expanded', open ? 'true' : 'false');
        }
    });

    // Body-class voor CSS-targeting buiten dit panel — bv. de sticky
    // header verbergen wanneer het drawer open is. Open: class toevoegen
    // bij start (zodat CSS direct kan reageren); close: class verwijderen
    // óók bij start zodat de sticky tijdens de drawer-fade-out al terug
    // begint te faden (smooth, geen harde knip aan het einde).
    document.body.classList.toggle('agora-mobile-nav-open', open);

    // Sticky-header heeft een eigen stacking-context (position: fixed +
    // z-index --z-sticky=100), waardoor de sticky-hamburger gevangen
    // zou worden onder de mobile-drawer (z-index --z-mobile-nav=400).
    // Met `is-drawer-open` breekt de toggle uit die context — SCSS zet
    // dan zelf `position: fixed` + `z-index: --z-menu-toggle=401` zodat
    // de close-X over de drawer komt. Zie `_site-header-sticky.scss`.
    document.querySelectorAll('.site-header-sticky [data-agora-menu-toggle]').forEach((t) => {
        if (!(t instanceof HTMLElement)) return;
        t.classList.toggle('is-drawer-open', open);
    });

    const dialog = panel.querySelector(SELECTORS.drawer);

    if (open) {
        // Volgorde: scroll-lock voor de animatie zodat scroll direct
        // bevroren is; panel zichtbaar maken; drawer animeren; items
        // staggered in-faden; daarna pas focus-trap activeren — anders
        // zou focus op een nog-uit-beeld element terechtkomen.
        lockScroll();
        panel.hidden = false;
        panel.setAttribute('aria-hidden', 'false');

        if (dialog instanceof HTMLElement) {
            // Pre-stagger: zet items op opacity 0 + translateY zodat de
            // post-slide stagger zichtbaar is. Door dit vóór de drawer-
            // animatie te doen voorkomen we een flash van zichtbare items
            // tijdens de slide-in.
            const items = dialog.querySelectorAll(STAGGER_SELECTOR);
            items.forEach((item) => {
                if (!(item instanceof HTMLElement)) return;
                item.style.opacity = '0';
                item.style.transform = 'translateY(10px)';
            });

            // Wis inline `transform`/`opacity` op de dialog die de
            // close-flow als startpositie heeft achtergelaten
            // (`translateX(100%)` + `opacity: 1`). Web Animations API
            // animeert via keyframes, maar na afloop valt het element
            // terug naar zijn inline style — bleef die staan, dan zou
            // de drawer na de slide-in direct weer 100% naar rechts
            // springen. Reset vóór de animatie zodat het eind-frame
            // schoon overblijft.
            dialog.style.removeProperty('transform');
            dialog.style.removeProperty('opacity');

            await motionAnimate(
                dialog,
                { transform: ['translateX(100%)', 'translateX(0)'] },
                { duration: 0.3, easing: 'ease-out' },
            );

            // Stagger de items één voor één in. Elke item krijgt een
            // delay gebaseerd op zijn index — Motion.js' duration-clamp
            // bij reduced motion zet duration én delay-effect op 0
            // gedragsmatig irrelevant (alle items springen meteen op
            // hun eindstate).
            items.forEach((item, index) => {
                if (!(item instanceof HTMLElement)) return;
                motionAnimate(
                    item,
                    {
                        opacity: [0, 1],
                        transform: ['translateY(10px)', 'translateY(0)'],
                    },
                    {
                        duration: 0.25,
                        delay: index * 0.04,
                        easing: 'ease-out',
                    },
                );
            });

            if (focusTrap) {
                focusTrap.deactivate();
            }
            focusTrap = createFocusTrap(dialog);
            // Geen aparte close-button meer (de hamburger zelf sluit).
            // Initial focus naar het eerste menu-item in de drawer; de
            // hamburger blijft via z-index boven het paneel zichtbaar
            // en sluit bij klik of via Escape.
            const firstLink = dialog.querySelector('a, button');
            focusTrap.activate(firstLink instanceof HTMLElement ? firstLink : null);
        }
        return; // valt door naar finally → isAnimating = false
    }

    // Close-volgorde: focus-trap eerst weg (focus terug naar toggle vóór
    // de drawer fade'd), dan fade-out animatie, dan panel hidden +
    // scroll-unlock + state-reset voor een schone volgende open.
    if (focusTrap) {
        focusTrap.deactivate();
        focusTrap = null;
    }

    if (dialog instanceof HTMLElement) {
        // Fade-out (i.p.v. slide-out) voelt minder abrupt op mobile waar
        // de drawer fullscreen was. We fade'n alleen de drawer-content;
        // het wrapper-panel wordt na de animatie via `hidden` verborgen.
        await fadeOut(dialog, { duration: 0.2 });

        // Reset transform/opacity zodat de volgende slideIn weer van
        // rechts kan beginnen. fadeOut heeft `hidden = true` op de
        // dialog gezet — dat moet weer false bij de volgende open
        // (gebeurt automatisch in de open-tak via `panel.hidden = false`
        // op het wrapper-element en het pre-stagger reset hieronder).
        dialog.hidden = false;
        dialog.setAttribute('aria-hidden', 'false');
        dialog.style.opacity = '1';
        dialog.style.transform = 'translateX(100%)';

        // Reset stagger inline-styles op alle items zodat ze bij de
        // volgende open weer correct vanaf opacity 0 + offset starten.
        dialog.querySelectorAll(STAGGER_SELECTOR).forEach((item) => {
            if (!(item instanceof HTMLElement)) return;
            item.style.removeProperty('opacity');
            item.style.removeProperty('transform');
        });
    }

    panel.hidden = true;
    panel.setAttribute('aria-hidden', 'true');
    unlockScroll();

    } finally {
        // Reset altijd, óók wanneer een animatie failde of een vroege
        // `return` is gedaan — anders kan een eenmalig falen de drawer
        // permanent dichtspijkeren voor de gebruiker.
        isAnimating = false;
    }
}

/**
 * Vindt de eerste toggle die in de huidige layout zichtbaar is. We
 * gebruiken `offsetParent` als snelle proxy voor "in de render-tree":
 * een element met `display: none` (of een ouder met dat) heeft
 * `offsetParent === null`. Wanneer geen enkele toggle zichtbaar is —
 * onverwacht maar mogelijk in test-omgevingen — valt de functie terug
 * op de eerste toggle.
 *
 * @param {NodeListOf<Element>|HTMLElement[]} toggles
 *
 * @returns {HTMLElement|null}
 */
function getVisibleToggle(toggles) {
    for (const t of toggles) {
        if (t instanceof HTMLElement && t.offsetParent !== null) {
            return t;
        }
    }
    const first = toggles[0];
    return first instanceof HTMLElement ? first : null;
}
